import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

const useStyles = makeStyles((theme) => ({
  groupMembersMentionWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: "1px solid",
    borderColor: theme.palette.background.paper,
    left: 0,
    width: "100%",
    maxHeight: "150px",
    overflowY: "auto",
    "& li": {
      listStyle: "none",
      "& span": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          backgroundColor: theme.palette.background.default,
          cursor: "pointer",
        },
      },
    },
  },

  groupMembersMentionWrapperItemAnchor: {
    color: theme.palette.text.primary,
  },

  selectedGroupMembersMentionWrapperItemAnchor: {
    backgroundColor: theme.palette.background.selectedTicket,
  },
}));

const GroupMembersMentionList = ({
  groupMembers,
  handleGroupMembersClick,
  typeAt,
  setTypeAt,
  filter
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const delimiter = "@";

  const groupMembersRef = useRef([]);

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [structuredFilter, setStructuredFilter] = useState("");



  //  ***********
  //  ** Memos **
  //  ***********
  const filteredGroupMembers = useMemo(() => {
    const lowerFilter = structuredFilter.toLowerCase();
    
    return groupMembers.filter(value => {
      const listItemMemberContent = value.name === DefaultValuesUtils.JIDS.allGroupMembers
        ? `${value.name}`
        : `${value.name} [${value.number}]`;

      const lowerListItemMemberContent = listItemMemberContent.toLowerCase();

      if (lowerFilter !== "" && !lowerListItemMemberContent.includes(lowerFilter)) return false;
      return true;
    });
  }, [groupMembers, structuredFilter]);



  //  *************
  //  ** Effects **
  //  *************
  useEffect(() => {
    if (!typeAt) return;
    const lastIndexOfDelimiter = filter.lastIndexOf(delimiter);
    setStructuredFilter(filter.substring(lastIndexOfDelimiter + 1));
  }, [typeAt, filter]);

  useEffect(() => {
    setFocusedIndex(0);
  }, [structuredFilter]);

  useEffect(() => {
    if (!typeAt) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex(previousIndex => previousIndex < filteredGroupMembers.length - 1 ? previousIndex + 1 : 0);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex(previousIndex => previousIndex > 0 ? previousIndex - 1 : filteredGroupMembers.length - 1);
      }

      if (event.key === "Enter" && filteredGroupMembers[focusedIndex]) {
        event.preventDefault();
        handleGroupMembersClick(filteredGroupMembers[focusedIndex].number);
        setTypeAt(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [typeAt, filteredGroupMembers, focusedIndex, setTypeAt, handleGroupMembersClick]);

  useEffect(() => {
    if (!typeAt) return;

    const handleClick = (event) => {
      const groupMembersMentionList = document.getElementById("groupMembersMentionList");
      const shouldHideGroupMembersMentionList = groupMembersMentionList && !(groupMembersMentionList.contains(event.target));
      
      if (shouldHideGroupMembersMentionList) setTypeAt(false);
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [typeAt, setTypeAt]);

  useEffect(() => {
    if (!typeAt) return;

    const currentGroupMember = groupMembersRef.current[focusedIndex];
    if (focusedIndex >= 0 && currentGroupMember) currentGroupMember.scrollIntoView({ block: "nearest" });
  }, [typeAt, focusedIndex]);



  //  ***************
  //  ** Functions **
  //  ***************
  const renderGroupMemberOption = (value, index) => {
    const className = index === focusedIndex
      ? `${classes.groupMembersMentionWrapperItemAnchor} ${classes.selectedGroupMembersMentionWrapperItemAnchor}`
      : `${classes.groupMembersMentionWrapperItemAnchor}`;

    const text = value.name === DefaultValuesUtils.JIDS.allGroupMembers
      ? `${i18n.t("groupMembersMentionList.allMembers")} [${i18n.t("groupMembersMentionList.allMembersDescription")}]`
      : `${value.name} [${value.number}]`

    return (
      <li key={index} ref={element => (groupMembersRef.current[index] = element)}>
        <span
          className={className}
          onClick={() => handleGroupMembersClick(value.number)}
          onMouseEnter={() => setFocusedIndex(index)}
        >
          {text}
        </span>
      </li>
    );
  };



  //  ************
  //  ** Return **
  //  ************
  if (!typeAt) return (<></>);

  return (
    <ul id ="groupMembersMentionList" className={classes.groupMembersMentionWrapper}>
      {filteredGroupMembers.map((value, index) => renderGroupMemberOption(value, index))}
    </ul>
  );
};

export default GroupMembersMentionList;
